import { Factory, PauseCircle } from 'lucide-react';
import { useState } from 'react';
import { useAdminMetrics, useCampaign, useUpdateCampaign } from '../../api/admin-queries.js';
import { useCatalog } from '../../api/queries.js';
import type { KitOffer } from '../../api/types.js';
import { Badge } from '../badge.js';
import { ErrorState } from '../error-state.js';

const DEFAULT_RESTOCK = 5;
/** One click should not commit the warehouses to more than this (reception holds 200 units). */
const MAX_RESTOCK_PER_ACTION = 50;

interface KitRow {
  readonly kit: KitOffer;
  readonly produced: number;
  readonly target: number;
  readonly pending: number;
}

function RestockControl({ row, onRestock }: { row: KitRow; onRestock: (n: number) => void }) {
  const [quantity, setQuantity] = useState(DEFAULT_RESTOCK);
  const inputId = `restock-${row.kit.sku}`;
  return (
    <div className="flex items-center justify-end gap-2">
      <label htmlFor={inputId} className="sr-only">
        Kits a producir de {row.kit.name}
      </label>
      <input
        id={inputId}
        type="number"
        min={1}
        max={MAX_RESTOCK_PER_ACTION}
        value={quantity}
        onChange={(event) => {
          setQuantity(Number(event.target.value));
        }}
        className="min-h-11 w-20 rounded-control border border-border bg-card px-2 text-right"
      />
      <button
        type="button"
        disabled={!Number.isInteger(quantity) || quantity < 1 || quantity > MAX_RESTOCK_PER_ACTION}
        onClick={() => {
          onRestock(quantity);
        }}
        className="inline-flex min-h-11 cursor-pointer items-center gap-1 rounded-control bg-accent px-3 font-medium text-on-accent transition-opacity duration-150 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Factory aria-hidden="true" className="size-4" />
        Producir
      </button>
    </div>
  );
}

export function KitProductionTable() {
  const catalog = useCatalog();
  const campaign = useCampaign();
  const metrics = useAdminMetrics();
  const update = useUpdateCampaign();

  if (catalog.isError || campaign.isError || metrics.isError)
    return <ErrorState message="No pudimos cargar la producción de kits." />;
  if (catalog.data === undefined || campaign.data === undefined || metrics.data === undefined)
    return <p aria-live="polite">Cargando producción…</p>;

  const targets = campaign.data.targets;
  const rows: KitRow[] = catalog.data.kits.map((kit) => {
    const produced = metrics.data.conditionedBySku[kit.sku] ?? 0;
    const target = targets[kit.sku] ?? 0;
    return { kit, produced, target, pending: Math.max(0, target - produced) };
  });
  const anyPending = rows.some((row) => row.pending > 0);

  function restock(row: KitRow, quantity: number): void {
    update.mutate({
      targets: { ...targets, [row.kit.sku]: Math.max(row.target, row.produced) + quantity },
    });
  }

  function stopPending(): void {
    if (!window.confirm('¿Detener la producción pendiente de todos los kits?')) return;
    update.mutate({
      targets: Object.fromEntries(
        rows.map((row) => [row.kit.sku, Math.min(row.target, row.produced)]),
      ),
    });
  }

  return (
    <section aria-labelledby="production-title" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="production-title" className="text-xl font-bold">
            Reponer stock
          </h2>
          <p className="max-w-prose text-sm text-muted-foreground">
            Los kits no se compran hechos: al pedir más, el sistema compra a Farma Central los
            insumos que falten, produce los intermedios y arma los kits (minutos a horas, según la
            cantidad).
          </p>
        </div>
        <button
          type="button"
          disabled={!anyPending || update.isPending}
          onClick={stopPending}
          className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-control border border-border px-4 font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PauseCircle aria-hidden="true" className="size-4" />
          Detener lo pendiente
        </button>
      </div>
      {update.isError && (
        <p role="alert" className="font-medium text-destructive">
          No se pudo guardar el cambio. Intenta de nuevo.
        </p>
      )}
      <div className="overflow-x-auto rounded-card border border-border bg-card">
        <table className="w-full min-w-[42rem] text-left">
          <thead className="bg-muted text-sm text-muted-foreground">
            <tr>
              <th scope="col" className="px-4 py-3 font-medium">
                Kit
              </th>
              <th scope="col" className="px-4 py-3 text-right font-medium">
                En stock
              </th>
              <th scope="col" className="px-4 py-3 text-right font-medium">
                Producidos
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                Estado
              </th>
              <th scope="col" className="px-4 py-3 text-right font-medium">
                Producir más
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.kit.sku} className="border-t border-border">
                <th scope="row" className="px-4 py-3 font-medium">
                  {row.kit.name}
                  <span className="block text-sm font-normal text-muted-foreground">
                    {row.kit.sku}
                  </span>
                </th>
                <td className="px-4 py-3 text-right tabular-nums">{row.kit.stock}</td>
                <td className="px-4 py-3 text-right tabular-nums">{row.produced}</td>
                <td className="px-4 py-3">
                  {row.pending > 0 ? (
                    <Badge tone="accent">Produciendo {row.pending}</Badge>
                  ) : (
                    <Badge tone="success">Al día</Badge>
                  )}
                </td>
                <td className="px-4 py-3">
                  <RestockControl
                    row={row}
                    onRestock={(quantity) => {
                      restock(row, quantity);
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
